import { SignalWatcher } from "@lit-labs/signals";
import { colorSchemeLabels, colorSchemes } from "@pentacode/core";
import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import {
    EXPERIMENTAL_DARK_SCHEME,
    experimentalColorSchemeLabel,
    type AppColorScheme,
} from "../../contexts/theme-context";
import { shared } from "../../styles";

export type ColorSchemeChangeEvent = CustomEvent<AppColorScheme>;

@customElement("ptc-color-scheme-select")
export class ColorSchemeSelect extends SignalWatcher(LitElement) {
    @property()
    value: AppColorScheme = "system";

    /** Adds the experimental token-based dark mode option. Dev-only; set by the signal watcher. */
    @property({ type: Boolean })
    experimental = false;

    private _onColorSchemeChange(e: Event) {
        const value = (e.target as HTMLSelectElement).value as AppColorScheme;
        this.value = value;
        this.dispatchEvent(
            new CustomEvent<AppColorScheme>("color-change", {
                detail: value,
            })
        );
    }

    static styles = [
        shared,
        css`
            :host {
                display: inline-block;
                font: inherit;
            }

            select {
                padding: 0.5em 0.75em;
                border: 1px solid var(--shade-2);
                border-radius: 0.25em;
                background-color: var(--background);
                color: var(--text);
                cursor: pointer;
                font-family: inherit;
                font-size: inherit;
                flex: 1;
            }

            select:focus {
                outline: none;
                border-color: var(--blue);
            }

            .select-wrapper {
                flex: 1;
                position: relative;
                display: flex;
                align-items: center;
                padding: 0;
                gap: 0.25em;
                font: inherit;
            }

            .select-wrapper select {
                padding-right: 2rem;
            }

            option {
                padding: 0.5em;
            }
        `,
    ];

    render() {
        const schemes: AppColorScheme[] = this.experimental
            ? [...colorSchemes, EXPERIMENTAL_DARK_SCHEME]
            : [...colorSchemes];

        return html`
            <label for="color-scheme" class="select-wrapper" title="Farbschema">
                <slot></slot>
                <select aria-label=${colorSchemeLabels.aria} @change=${this._onColorSchemeChange} .value=${this.value}>
                    ${schemes.map(
                        (scheme) =>
                            html` <option ?selected=${scheme === this.value} value=${scheme}>
                                ${scheme === EXPERIMENTAL_DARK_SCHEME
                                    ? experimentalColorSchemeLabel
                                    : colorSchemeLabels[scheme]}
                            </option>`
                    )}
                </select>
            </label>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ptc-color-scheme-select": ColorSchemeSelect;
    }
}
